import { selectorFamily, useRecoilValue } from 'recoil';
import { filterByStatus, isDateInRange } from 'functions';
import { invoiceFilter, invoiceSummaries, useSetRefreshState } from 'state';
import { InvoiceSummary } from 'types';
import { HasAdjustments } from 'enums';
import Fuse from 'fuse.js';

export const filteredInvoices = selectorFamily<InvoiceSummary[], string>({
    key: 'filteredInvoices',
    get: (accountId) =>
        async ({ get }) => {
            const filter = get(invoiceFilter);
            const {
                dateRange, 
                invoicesStartAmount, 
                invoicesEndAmount, 
                invoiceId,
                status,
                poNumber,
                checkNumber,
                hasAdjustments,
                adjustmentStartAmount, 
                adjustmentEndAmount, 
                searchKeys,
                searchTerm,
                sort
            } = filter;

            const invoices = get(invoiceSummaries({ accountId, dateRange }));

            const list = invoices.filter((invoiceSummary) => {
                const {
                    dotPriceAdjustment,
                    dotQuantityAdjustment,
                    invoiceDate,
                    invoiceId: id,
                    paymentIdRef,
                    userVoucherDecimal,
                    voucherPoId,
                } = invoiceSummary;
                
                return (isDateInRange(dateRange, invoiceDate) 
                    && (invoicesStartAmount ? userVoucherDecimal >= invoicesStartAmount : true)
                    && (invoicesEndAmount ? userVoucherDecimal <= invoicesEndAmount : true)
                    && (status ? filterByStatus(status, invoiceSummary) : true)
                    && (invoiceId ? id?.startsWith(invoiceId) : true)
                    && (checkNumber ? paymentIdRef?.startsWith(checkNumber) : true)
                    && (poNumber ? voucherPoId?.startsWith(poNumber) : true)
                    && (hasAdjustments ? filterHasAdustments(hasAdjustments, invoiceSummary) : true)
                    && (adjustmentStartAmount ? (dotPriceAdjustment + dotQuantityAdjustment) >= adjustmentStartAmount : true)
                    && (adjustmentEndAmount ? (dotPriceAdjustment + dotQuantityAdjustment) <= adjustmentEndAmount : true)
                );
            });

            
            if(searchTerm && searchTerm !== ""){
                const fuse = new Fuse(list, { keys: searchKeys as string[], threshold: 0.3, ignoreLocation: true });

                return fuse
                        .search(searchTerm)
                        .map((r) => r.item)
                        .sort(sort.compare)
            }

            return list.sort(sort.compare);
        }
});

const filterHasAdustments = (hasAdjustments: HasAdjustments, { dotPriceAdjustment, dotQuantityAdjustment } : InvoiceSummary) => {
    switch (hasAdjustments) {
        case HasAdjustments.HasAdjustments: return ((dotPriceAdjustment !== 0 || dotQuantityAdjustment) !== 0);
        case HasAdjustments.NoAdjustments: return ((dotPriceAdjustment + dotQuantityAdjustment) === 0);
        default: return true;
    }
};

export const useFilteredInvoices = (accountId: string) => useRecoilValue(filteredInvoices(accountId));

export const useRefreshFilteredInvoices = (accountId: string) => {
    const { dateRange } = useRecoilValue(invoiceFilter);
    return useSetRefreshState(invoiceSummaries({ accountId, dateRange }));
};
